import { makeAutoObservable, runInAction } from "mobx";
// import * as FileSystem from "expo-file-system";
// import HapticManager from "./HapticManager";
// import ChatManager from "./ChatManager";
import { v4 as uuidv4 } from "uuid";
import ChatMessage from "../Firestore/ChatMessage";
import ChatManager from "./ChatManager";
import { Chat, MessageStatus } from "../../Resources/Constants";
import NetworkManager from "./NetworkManager";

class AudioManager {
  static instance = null;

  constructor() {
    if (AudioManager.instance) {
      return AudioManager.instance;
    }
    AudioManager.instance = this;

    /* AUDIO RECORDING */
    this.audioRecorder = null;
    this.recordedChunks = [];
    this.mediaStream = null;
    this.isRecording = false;
    this.recordingStartTime = null;
    this.recordingTimer = null;
    this.recordingTime = 0;
    this.soundSamples = [];
    this.localURL = null;
    this.audioUUID = uuidv4();
    this.attachmentFile = null;

    /* AUDIO PLAYER */
    this.audioPlayer = null;
    this.audioMessagePlayingId = null;
    this.isAudioPlaying = false;
    this.playbackTimer = 0;
    this.audioPositionValue = 0;

    // Playback speed control
    this.playbackRates = [1.0, 1.5, 2.0];
    this.currentRateIndex = 0;
    this.playbackRate = this.playbackRates[this.currentRateIndex];

    makeAutoObservable(this);
  }

  static getInstance() {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  setProperty(propertyName, value) {
    if (propertyName in this) {
      this[propertyName] = value;
    } else {
      console.warn(
        `Property "${propertyName}" does not exist on AudioManager.`
      );
    }
  }

  // // #region /* AUDIO RECORDING */
  async startRecording() {
    // 1) Tear down any previous recorder/stream/context
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
      this.analyserNode = null;
      this.dataArray = null;
    }

    // create and resume one AudioContext
    const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioCtxClass();
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    // now do the rest
    await this._startRecording();
  }

  async _startRecording() {
    clearInterval(this.recordingTimer);
    this.isRecording = false;

    // 2) Grab a fresh mic stream
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
    } catch (err) {
      const chatManager = ChatManager.getInstance();
      chatManager.setProperty("showingPermissionsAlert", true);
      console.error("Mic permission denied:", err);
      return;
    }

    // 3) Configure encoding: prefer Opus in WebM, fallback to AAC/MP4
    this.recordedChunks = [];
    this.audioUUID = uuidv4();
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
      ? "audio/webm"
      : MediaRecorder.isTypeSupported("audio/mp4")
      ? "audio/mp4"
      : undefined;

    // Set a target bitrate of ~32 kbps to reduce file size (~240 KB/minute)
    const options = {};
    if (mimeType) {
      options.mimeType = mimeType;
    }
    options.audioBitsPerSecond = 16000; // 32 kbps

    let recorder;
    try {
      recorder = new MediaRecorder(this.mediaStream, options);
    } catch (err) {
      console.error(
        "Failed to create MediaRecorder with options:",
        options,
        err
      );
      return;
    }
    this.audioRecorder = recorder;

    this.audioRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size) this.recordedChunks.push(e.data);
    };
    // Note: we will handle onstop in stopRecording to avoid clobbering

    // 4) Metering setup via Web Audio API
    // const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
    // this.audioContext = new AudioCtxClass();
    const sourceNode = this.audioContext.createMediaStreamSource(
      this.mediaStream
    );
    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = 256;
    sourceNode.connect(this.analyserNode);
    this.dataArray = new Float32Array(this.analyserNode.fftSize);

    // 5) Start recording + meter timer every 100 ms
    this.audioRecorder.start(1000);
    // this.audioRecorder.start();
    this.recordingStartTime = Date.now();
    this.setProperty("isRecording", true);
    this.recordingTime = 0;
    this.soundSamples = [];

    this.recordingTimer = setInterval(() => {
      // a) update elapsed time
      this.setProperty(
        "recordingTime",
        (Date.now() - this.recordingStartTime) / 1000
      );

      // b) pull PCM data
      if (this.analyserNode && this.dataArray) {
        this.analyserNode.getFloatTimeDomainData(this.dataArray);
        let sumSquares = 0;
        for (let i = 0; i < this.dataArray.length; i++) {
          sumSquares += this.dataArray[i] * this.dataArray[i];
        }
        const rms = Math.sqrt(sumSquares / this.dataArray.length);

        // c) convert to dB & normalize
        let dB = 20 * Math.log10(rms);
        if (!isFinite(dB)) dB = -60;
        const minDb = -60,
          maxDb = 0;
        let norm = (dB - minDb) / (maxDb - minDb);
        norm = Math.min(1, Math.max(0, norm));
        norm = Math.sqrt(norm);

        // d) store sample
        runInAction(() => {
          this.soundSamples.push(norm);
        });
      } else {
        console.warn("No analyserNode or dataArray for metering.");
      }
    }, 100);
  }

  async stopRecording(client) {
    if (!this.audioRecorder || !this.isRecording) return;

    // 1) Stop & wait for "stop" event without overwriting onstop
    const stopPromise = new Promise((res) => {
      this.audioRecorder.addEventListener("stop", res, { once: true });
    });
    this.audioRecorder.stop();
    clearInterval(this.recordingTimer);
    await stopPromise;

    // 2) CLEAN UP the mic stream & audio context
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
      this.analyserNode = null;
      this.dataArray = null;
    }

    this.setProperty("isRecording", false);

    // 3) Downsample to 8 kHz mono then encode WAV for seekability at ~1 MB/min
    const rawBlob = new Blob(this.recordedChunks, { type: "audio/webm" });
    const arrayBuffer = await rawBlob.arrayBuffer();

    // Decode original audio
    const decodeCtx = new (window.AudioContext || window.webkitAudioContext)();
    const originalBuffer = await decodeCtx.decodeAudioData(arrayBuffer);
    decodeCtx.close();

    // Offline render at 8 kHz mono
    const targetRate = 16000;
    const offlineCtx = new OfflineAudioContext(
      1,
      originalBuffer.duration * targetRate,
      targetRate
    );
    const src = offlineCtx.createBufferSource();
    src.buffer = originalBuffer;
    src.connect(offlineCtx.destination);
    src.start(0);
    const downsampled = await offlineCtx.startRendering();

    // Helper to encode WAV with RIFF header and index
    function encodeWAV(buffer) {
      const numChan = buffer.numberOfChannels;
      const length = buffer.length * numChan * 2 + 44;
      const view = new DataView(new ArrayBuffer(length));
      let offset = 0;
      function writeString(s) {
        for (let i = 0; i < s.length; i++) {
          view.setUint8(offset++, s.charCodeAt(i));
        }
      }
      function write16(d) {
        view.setInt16(offset, d, true);
        offset += 2;
      }
      function write32(d) {
        view.setUint32(offset, d, true);
        offset += 4;
      }
      writeString("RIFF");
      write32(length - 8);
      writeString("WAVEfmt ");
      write32(16);
      write16(1);
      write16(numChan);
      write32(buffer.sampleRate);
      write32(buffer.sampleRate * numChan * 2);
      write16(numChan * 2);
      write16(16);
      writeString("data");
      write32(length - offset - 4);
      // interleave channels
      const interleaved = new Float32Array(buffer.length * numChan);
      for (let c = 0; c < numChan; c++) {
        const chanData = buffer.getChannelData(c);
        for (let i = 0; i < chanData.length; i++) {
          interleaved[i * numChan + c] = chanData[i];
        }
      }
      // write PCM samples
      for (let i = 0; i < interleaved.length; i++) {
        const s = Math.max(-1, Math.min(1, interleaved[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        offset += 2;
      }
      return new Blob([view], { type: "audio/wav" });
    }
    const wavBlob = encodeWAV(downsampled);
    // 4) Create a preview URL pointing to the WAV
    this.localURL = URL.createObjectURL(wavBlob);
    // 5) Wrap in a File for uploads
    this.attachmentFile = new File([wavBlob], `${this.audioUUID}.wav`, {
      type: "audio/wav",
    });

    // 6) Create message and upload it
    if (this.recordingTime > 1 && this.attachmentFile) {
      try {
        const chatManager = ChatManager.getInstance();
        const networkManager = NetworkManager.getInstance();
        const timestamp = networkManager.getNetworkTime();
        const newMessage = new ChatMessage(
          undefined,
          timestamp,
          undefined,
          "audio",
          true,
          chatManager.selectedReplyToId,
          this.attachmentFile,
          this.recordingTime,
          undefined,
          undefined,
          undefined,
          client,
          MessageStatus.SENDING,
          this.localURL
        );
        await client.enqueueOutgoingChatMessages([newMessage]);
        chatManager.setProperty("selectedReplyToId", null);
        chatManager.scrollToBottom();
      } catch (uploadError) {
        console.error("Error uploading audio message:", uploadError);
      }
    }

    // 7) Reset flags
    this.recordingTimer = null;
    this.recordingTime = (Date.now() - this.recordingStartTime) / 1000;
  }

  async deleteRecording() {
    // 1) If we’re still recording, stop & wait for the “stop” event
    if (this.audioRecorder) {
      const stopped = new Promise((res) =>
        this.audioRecorder.addEventListener("stop", res, { once: true })
      );
      try {
        this.audioRecorder.stop();
      } catch (e) {
        console.warn("deleteRecording: error stopping recorder", e);
      }
      await stopped;
      this.audioRecorder = null;
    }

    // 2) Clear the metering timer
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }

    // 3) Tear down the mic stream
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }

    // 4) Close the AudioContext
    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch (e) {
        console.warn("deleteRecording: error closing AudioContext", e);
      }
      this.audioContext = null;
      this.analyserNode = null;
      this.dataArray = null;
    }

    // 5) Revoke any object URL for playback
    if (this.localURL) {
      URL.revokeObjectURL(this.localURL);
      this.localURL = null;
    }

    // 6) Clear out your blobs & file handle
    this.recordedChunks = [];
    this.attachmentFile = null;
    this.recordingTime = 0;

    // 7) Reset flags
    this.setProperty("isRecording", false);

    // 8) Fresh UUID for the next recording
    this.audioUUID = uuidv4();
  }

  isSameAudioMessage(message) {
    return this.audioMessagePlayingId?.toString() === message.id.toString();
  }

  async debugAudio(url) {
    // 1) HEAD + MIME
    let head;
    try {
      head = await fetch(url, { method: "HEAD" });
    } catch (e) {
      console.error("❌ HEAD failed for", url, e);
      // skip MIME check if HEAD itself fails
    }

    const ct = head?.headers.get("content-type") || "(no MIME)";
    console.log(`🔍 HEAD ${url} → ${head?.status || "-"} ${ct}`);

    // 2) Probe canPlayType on an element instance
    const probe = document.createElement("audio");
    console.log("probe.canPlayType(served):", probe.canPlayType(ct));
    console.log(
      "probe.canPlayType(audio/mp4):",
      probe.canPlayType("audio/mp4")
    );
    console.log(
      "probe.canPlayType(audio/m4a):",
      probe.canPlayType("audio/m4a")
    );

    // 3) Fetch the blob and inspect
    let blob;
    try {
      const res = await fetch(url);
      blob = await res.blob();
      console.log("🔍 BLOB", blob.type, blob.size, "bytes");
    } catch (e) {
      console.error("❌ Fetch as blob failed:", e);
    }

    // 4) Try playing via object URL
    if (blob) {
      const objectURL = URL.createObjectURL(blob);
      const tester = new Audio(objectURL);
      tester.preload = "auto";
      tester.addEventListener("error", () => {
        console.error("MediaError.code:", tester.error?.code);
      });
      try {
        await tester.play();
        console.log("✅ Played via ObjectURL");
      } catch (e) {
        console.error("❌ play() failed:", e);
      }
    }
  }

  async playRecording(message) {
    let attachmentURL;
    // 1) Get the URL for the audio attachment
    try {
      attachmentURL = await message.getAttachmentURL();
    } catch (err) {
      console.error("Error getting attachment URL:", err);
      throw err;
    }
    console.log("download URL is:", attachmentURL);
    // await this.debugAudio(attachmentURL);
    // 2) If we're already paused on this same URL, just resume
    if (
      this.audioPlayer &&
      this.audioPlayer.src === attachmentURL &&
      this.audioPlayer.paused
    ) {
      try {
        if (this.isSameAudioMessage(message)) {
          await this.resumePlayback();
        } else {
          this.setProperty("audioMessagePlayingId", message.id.toString());
          this.setProperty("audioPositionValue", 0);
          await this.resumePlayback();
        }
      } catch (err) {
        console.error("Error resuming playback:", err);
        throw err;
      }
      return;
    }

    // 2b) If it's the same message but we have no player (or it ended),
    // resume from the last known position
    if (this.isSameAudioMessage(message)) {
      // Create a new <audio> element if one doesn't exist
      this.audioPlayer = new Audio(attachmentURL);
      this.audioPlayer.preload = "auto";
      this.audioPlayer.playbackRate = this.playbackRate;
      try {
        this.audioPlayer.currentTime = this.audioPositionValue || 0;
        await this.audioPlayer.play();
        runInAction(() => {
          this.isAudioPlaying = true;
        });
        this.startPositionUpdater();
      } catch (err) {
        console.error("Error resuming playback at position:", err);
        throw err;
      }
      return;
    }

    // 3) Otherwise, it's a new message → tear down any previous player
    this.stopPositionUpdater();
    if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer.src = "";
      this.audioPlayer = null;
    }

    // 4) Reset state for the new message (start at 0)
    runInAction(() => {
      this.audioMessagePlayingId = message.id.toString();
      this.audioPositionValue = 0;
      this.isAudioPlaying = false;
    });

    // 5) Create a new HTMLAudioElement and play it from 0
    this.audioPlayer = new Audio(attachmentURL);
    this.audioPlayer.preload = "auto";
    this.audioPlayer.playbackRate = this.playbackRate;
    try {
      await this.audioPlayer.play();
      runInAction(() => {
        this.isAudioPlaying = true;
      });
      this.startPositionUpdater();
    } catch (err) {
      console.error("Error playing audio:", err);
      throw err;
    }
  }

  // Resumes playback from the last stored position
  async resumePlayback() {
    if (!this.audioPlayer) return;
    try {
      // seek to stored time
      this.audioPlayer.load();
      this.audioPlayer.currentTime = this.audioPositionValue || 0;
      this.audioPlayer.playbackRate = this.playbackRate;
      await this.audioPlayer.play();
      runInAction(() => {
        this.isAudioPlaying = true;
      });
      this.startPositionUpdater();
    } catch (err) {
      console.error("Error resuming playback:", err);
    }
  }

  // Resets your MobX state when switching to a new message
  resetAudioState(message) {
    if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer.src = "";
    }
    this.audioPlayer = null;
    runInAction(() => {
      this.audioMessagePlayingId = message.id.toString();
      this.audioPositionValue = 0;
      this.isAudioPlaying = false;
    });
  }

  // Sets up timeupdate and ended handlers on the <audio> element
  startPositionUpdater() {
    if (!this.audioPlayer) return;

    // Clear any existing handlers
    this.stopPositionUpdater();

    // Update position every time the audio element fires `timeupdate`
    this.audioPlayer.ontimeupdate = () => {
      runInAction(() => {
        this.audioPositionValue = this.audioPlayer.currentTime;
      });
    };

    // When the audio finishes, reset position & playing flag
    this.audioPlayer.onended = () => {
      runInAction(() => {
        this.audioPositionValue = 0;
        this.isAudioPlaying = false;
      });
      this.stopPositionUpdater();
    };
  }

  // Removes any attached handlers so we stop updating
  stopPositionUpdater() {
    if (!this.audioPlayer) return;
    this.audioPlayer.ontimeupdate = null;
    this.audioPlayer.onended = null;
  }

  pauseRecording() {
    if (!this.audioPlayer) {
      return;
    }

    // 1) Pause playback
    try {
      this.audioPlayer.pause();
    } catch (err) {
      console.error("Error pausing audio playback:", err);
    }

    // 2) Stop updating the position
    this.stopPositionUpdater();

    // 3) Store the current position
    runInAction(() => {
      this.audioPositionValue = this.audioPlayer.currentTime;
      this.isAudioPlaying = false;
    });
  }

  // async getAudioRecording(
  //   message,
  //   shouldPlay = true,
  //   startingPosition = this.audioPositionValue
  // ) {
  //   let audioUri = message.localURL();

  //   if (audioUri) {
  //     try {
  //       // Create a new Audio.Sound instance, starting at the audioPositionValues.
  //       const { sound, status } = await Audio.Sound.createAsync(
  //         { uri: audioUri },
  //         {
  //           shouldPlay: shouldPlay,
  //           positionMillis: startingPosition * 1000,
  //           progressUpdateIntervalMillis: 100,
  //         }
  //       );
  //       console.log("shouldPlay", shouldPlay);
  //       runInAction(() => {
  //         this.isAudioPlaying = shouldPlay;
  //         this.audioPlayer = sound;
  //         this.audioMessagePlayingId = message.id.toString();
  //       });
  //       this.startPositionUpdater();
  //     } catch (error) {
  //       console.error("Playback failed:", error);
  //       throw Error("Playback failed:", error);
  //     }
  //   } else {
  //     console.error("No local audio URL available.");
  //   }
  // }

  getAudioTime(message) {
    if (this.isSameAudioMessage(message)) {
      return Math.max(0, message.totalTime - this.audioPositionValue);
    } else {
      return message.totalTime;
    }
  }

  async setPlaybackRate(rate) {
    this.playbackRate = rate;
    if (this.audioPlayer) {
      try {
        this.audioPlayer.playbackRate = rate;
      } catch (e) {
        console.error("Error setting playback rate:", e);
      }
    }
  }

  /**
   * Cycle through available playback rates (1x → 1.5x → 2x → 1x).
   */
  async cyclePlaybackRate() {
    this.currentRateIndex =
      (this.currentRateIndex + 1) % this.playbackRates.length;
    const newRate = this.playbackRates[this.currentRateIndex];
    await this.setPlaybackRate(newRate);
  }
  // // #endregion
}

export default AudioManager;
